/**
 * 斗兽棋游戏组件
 * 9×7棋盘，8种动物，通过吃子推进，占领对方巢穴或吃光对方棋子获胜
 */

import React, { useState, useCallback, useEffect } from 'react';
import { GameMode, Difficulty, Player, Position, ANIMAL_NAMES } from '../types/game';
import { JungleGameState } from '../types/game';
import {
  createInitialGameState,
  selectAnimal,
  deselectAnimal,
  makeMove,
  checkGameEnd
} from '../utils/jungle';
import { getJungleAIDecision, simulateJungleAIThinking } from '../utils/jungleAI';
import JungleBoard from './JungleBoard';
import VictoryEffect from './VictoryEffect';
import GameShareButton from './GameShareButton';
import { cn } from '../lib/utils';

interface JungleGameProps {
  gameMode: GameMode;
  difficulty?: Difficulty;
  onBackToMenu?: () => void;
  onBackToModeSelect?: () => void;
  onGameEnd?: (winner: Player, moves: number) => void;
}

const JungleGame: React.FC<JungleGameProps> = ({
  gameMode,
  difficulty = 'medium',
  onBackToMenu,
  onBackToModeSelect,
  onGameEnd
}) => {
  const [gameState, setGameState] = useState<JungleGameState>(() =>
    createInitialGameState(gameMode, difficulty)
  );
  const [gameMessage, setGameMessage] = useState<string>('');
  const [lastWinner, setLastWinner] = useState<Player | null>(null);
  const [showVictoryEffect, setShowVictoryEffect] = useState(false);
  const [isBoardEnlarged, setIsBoardEnlarged] = useState<boolean>(false);
  const [showEndGameDialog, setShowEndGameDialog] = useState<boolean>(false);
  const [dialogManuallyClosed, setDialogManuallyClosed] = useState<boolean>(false);
  const [showRules, setShowRules] = useState<boolean>(false);
  const [canUndo, setCanUndo] = useState(false);

  // AI回合处理
  const handleAITurn = useCallback(async (currentState: JungleGameState) => {
    if (gameMode !== 'pvc' || currentState.currentPlayer !== 'white' || currentState.status !== 'playing') {
      return;
    }

    setGameState(prev => ({ ...prev, isAIThinking: true }));

    // 根据难度显示不同的思考消息
    const difficultyText = difficulty === 'easy' ? '简单' : difficulty === 'medium' ? '中等' : '困难';
    setGameMessage(`🤖 白方AI正在思考 (${difficultyText}难度)...`);

    await simulateJungleAIThinking(difficulty);

    const aiMove = getJungleAIDecision(currentState, difficulty);
    if (aiMove) {
      const animal = currentState.board[aiMove.from.row][aiMove.from.col];
      const animalName = animal ? ANIMAL_NAMES[animal.type] : '动物';

      let moveDescription = '';
      if (aiMove.isRiverJump) {
        moveDescription = `${animalName}跳河移动`;
      } else if (aiMove.captures && aiMove.captures.length > 0) {
        const capturedAnimal = aiMove.captures[0];
        const capturedName = ANIMAL_NAMES[capturedAnimal.type];
        moveDescription = `${animalName}吃掉了${capturedName}`;
      } else {
        moveDescription = `${animalName}移动`;
      }

      const newGameState = makeMove(currentState, aiMove);
      setGameState(newGameState);
      setGameMessage(`🎯 AI移动：${moveDescription}`);

      // 检查游戏是否结束
      const gameEnd = checkGameEnd(newGameState);
      if (gameEnd.isEnd) {
        handleGameEnd(gameEnd.winner!, gameEnd.reason);
      }
    } else {
      setGameMessage('⚠️ 白方AI无法移动，跳过回合');
    }

    setGameState(prev => ({ ...prev, isAIThinking: false }));
    setTimeout(() => setGameMessage(''), 2000);
  }, [gameMode, difficulty]);

  // 棋盘点击处理
  const handleCellClick = useCallback((position: Position) => {
    if (gameState.status !== 'playing' || gameState.isAIThinking) return;

    const clickedAnimal = gameState.board[position.row][position.col];

    // 如果点击的是当前玩家的动物
    if (clickedAnimal && clickedAnimal.player === gameState.currentPlayer) {
      const newGameState = selectAnimal(gameState, position);
      setGameState(newGameState);
      return;
    }

    // 如果已选中动物，尝试移动
    if (gameState.selectedPiece) {
      const possibleMove = gameState.possibleMoves.find(
        move => move.to.row === position.row && move.to.col === position.col
      );

      if (possibleMove) {
        const newGameState = makeMove(gameState, possibleMove);
        setGameState(newGameState);

        // 检查游戏是否结束
        const gameEnd = checkGameEnd(newGameState);
        if (gameEnd.isEnd) {
          handleGameEnd(gameEnd.winner!, gameEnd.reason);
          return;
        }

        // AI回合
        if (gameMode === 'pvc' && newGameState.currentPlayer === 'white') {
          setTimeout(() => handleAITurn(newGameState), 1000);
        } else {
          setGameMessage(`${newGameState.currentPlayer === 'black' ? '黑方' : '白方'}回合`);
          setTimeout(() => setGameMessage(''), 2000);
        }
      } else {
        // 点击无效位置，取消选择
        const newGameState = deselectAnimal(gameState);
        setGameState(newGameState);
      }
    }
  }, [gameState, gameMode, handleAITurn]);

  // 游戏结束处理
  const handleGameEnd = useCallback((winner: Player, reason: string) => {
    setGameState(prev => ({
      ...prev,
      status: 'finished',
      winner
    }));

    setLastWinner(winner);
    setShowVictoryEffect(true);
    if (!dialogManuallyClosed) {
      setShowEndGameDialog(true);
    }
    setGameMessage(reason);

    onGameEnd?.(winner, gameState.totalMoves);
  }, [gameState.totalMoves, onGameEnd, dialogManuallyClosed]);

  // 重新开始
  const handleRestart = useCallback(() => {
    const newState = createInitialGameState(gameMode, difficulty);
    setGameState(newState);
    setGameMessage('黑方先行');
    setShowVictoryEffect(false);
    setShowEndGameDialog(false);
    setDialogManuallyClosed(false);
  }, [gameMode, difficulty]);

  // 悔棋
  const handleUndo = useCallback(() => {
    if (gameState.moves.length === 0) return;
    if (gameState.gameHistory.length === 0) return;

    const previousState = gameState.gameHistory[gameState.gameHistory.length - 1];
    setGameState(previousState);
    setGameMessage('悔棋成功');
    setTimeout(() => setGameMessage(''), 1500);
  }, [gameState]);

  // 重置游戏
  const handleReset = useCallback(() => {
    handleRestart();
    setGameMessage('游戏重置！');
    setTimeout(() => setGameMessage(''), 2000);
  }, [handleRestart]);

  // 切换放大模式
  const toggleBoardEnlarge = useCallback(() => {
    setIsBoardEnlarged(prev => {
      const newMode = !prev;
      setGameMessage(newMode ? '🔍 已切换到老人模式（放大棋盘）' : '📐 已恢复默认布局');
      setTimeout(() => setGameMessage(''), 2000);
      return newMode;
    });
  }, []);

  // 获取当前游戏消息
  const getCurrentGameMessage = () => {
    if (gameState.isAIThinking) {
      return gameMessage;
    }

    if (gameState.status === 'finished') {
      return gameMessage;
    }

    if (gameState.status === 'playing' && !gameState.isAIThinking) {
      // 检查是否选中了动物
      if (gameState.selectedPiece) {
        const selectedAnimal = gameState.board[gameState.selectedPiece.row][gameState.selectedPiece.col];
        if (selectedAnimal) {
          const moveCount = gameState.possibleMoves?.length || 0;
          const captureMoves = gameState.possibleMoves?.filter(m => m.captures && m.captures.length > 0).length || 0;
          const jumpMoves = gameState.possibleMoves?.filter(m => m.isRiverJump).length || 0;

          if (moveCount === 0) {
            return `⚠️ ${selectedAnimal.player === 'black' ? '黑方' : '白方'}的${ANIMAL_NAMES[selectedAnimal.type]}无法移动！`;
          } else if (jumpMoves > 0 && captureMoves > 0) {
            return `🎯 ${selectedAnimal.player === 'black' ? '黑方' : '白方'}${ANIMAL_NAMES[selectedAnimal.type]}：可跳河${jumpMoves}次，可吃子${captureMoves}次`;
          } else if (jumpMoves > 0) {
            return `🌊 ${selectedAnimal.player === 'black' ? '黑方' : '白方'}${ANIMAL_NAMES[selectedAnimal.type]}：可跳河${jumpMoves}次`;
          } else if (captureMoves > 0) {
            return `⚔️ ${selectedAnimal.player === 'black' ? '黑方' : '白方'}${ANIMAL_NAMES[selectedAnimal.type]}：可吃子${captureMoves}次`;
          } else {
            return `📍 ${selectedAnimal.player === 'black' ? '黑方' : '白方'}${ANIMAL_NAMES[selectedAnimal.type]}：有${moveCount}个移动选项`;
          }
        }
      }

      return `💡 ${gameState.currentPlayer === 'black' ? '黑方' : '白方'}回合 - 选择己方动物查看可用移动`;
    }

    return gameMessage || '游戏进行中';
  };

  // 键盘快捷键
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const newGameState = deselectAnimal(gameState);
        setGameState(newGameState);
      }
      if (event.key === 'z' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleUndo();
      }
      if (event.key === 'r' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        handleReset();
      }
      if (event.key === 'e' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        toggleBoardEnlarge();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState, handleUndo, handleReset, toggleBoardEnlarge]);

  // 游戏状态监听 - AI自动触发
  useEffect(() => {
    if (gameMode === 'pvc' && gameState.currentPlayer === 'white' && gameState.status === 'playing' && !gameState.isAIThinking) {
      const timer = setTimeout(() => {
        handleAITurn(gameState);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [gameState.currentPlayer, gameMode, gameState.status, gameState.isAIThinking, handleAITurn]);

  // 更新悔棋按钮状态
  useEffect(() => {
    setCanUndo(gameState.gameHistory.length > 0 && gameState.status === 'playing' && !gameState.isAIThinking);
  }, [gameState.gameHistory.length, gameState.status, gameState.isAIThinking]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex flex-col">
      {/* 游戏头部 - 统一布局 */}
      <div className="bg-white shadow p-2">
        {/* 标题区域 - 手机端独立行，桌面端居中 */}
        <div className="text-center mb-2 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center sm:space-x-0 sm:space-y-1 space-y-1">
            {/* 美化标题 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">🐯</span>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent sm:mb-1">
                斗兽棋
              </h1>
              <span className="text-2xl">🦁</span>
            </div>

            {/* 美化模式描述 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <span className="w-2 h-2 mr-1 bg-white rounded-full animate-pulse"></span>
                {gameMode === 'pvp' ? '双人对战' : `人机对战`}
              </span>

              {gameMode === 'pvc' && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                  difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {difficulty === 'easy' ? '😊 简单' : difficulty === 'medium' ? '🎯 中等' : '🔥 困难'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 控制按钮区域 */}
        <div className="flex items-center justify-between">
          {/* 左侧按钮组 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 首页按钮 */}
            {onBackToMenu && (
              <button onClick={onBackToMenu} className="flex items-center space-x-1 px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium" title="回到首页">
                <span>🏠</span>
                <span className="hidden sm:inline">首页</span>
              </button>
            )}
            {/* 返回按钮 */}
            {onBackToModeSelect && (
              <button onClick={onBackToModeSelect} className="flex items-center space-x-1 px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium" title="返回模式选择">
                <span>🔙</span>
                <span className="hidden sm:inline">返回</span>
              </button>
            )}

            {/* 重新开始按钮 */}
            <button onClick={handleReset} className="flex items-center space-x-1 px-2 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs font-medium" title="重新开始">
              <span>🔄</span>
              <span className="hidden sm:inline">重开</span>
            </button>

            {/* 悔棋按钮 */}
            <button onClick={handleUndo} disabled={!canUndo} className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
              canUndo ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`} title="悔棋">
              <span>↩️</span>
              <span className="hidden sm:inline">悔棋</span>
            </button>
          </div>

          {/* 右侧控制按钮 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 游戏规则按钮 */}
            <button onClick={() => setShowRules(true)} className="flex items-center space-x-1 px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium" title="游戏规则">
              <span>📖</span>
              <span className="hidden sm:inline">规则</span>
            </button>

            {/* 分享按钮 */}
            <GameShareButton gameType="jungle" />

            {/* 放大按钮 */}
            <button onClick={toggleBoardEnlarge} className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
              isBoardEnlarged ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-green-500 text-white hover:bg-green-600'
            }`} title={isBoardEnlarged ? "恢复正常布局" : "放大棋盘"}>
              <span>🔍</span>
              <span className="hidden sm:inline">{isBoardEnlarged ? '恢复' : '放大'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 游戏状态信息 - 统一布局 */}
      {gameState.status === 'playing' && (
        <div className="bg-white shadow p-2">
          <div className="flex items-center justify-between">
            {/* 当前玩家信息 */}
            <div className="flex items-center space-x-1 sm:space-x-1">
              <div className={`w-4 h-4 rounded-full ${
                gameState.currentPlayer === 'black' ? 'bg-gray-900' : 'bg-gray-100 border-2 border-gray-400'
              }`}></div>
              <span className="text-sm sm:text-base font-medium">
                {gameState.currentPlayer === 'black' ? '黑方' : '白方'}回合
              </span>
              {gameState.isAIThinking && (
                <span className="text-xs text-blue-600 animate-pulse ml-2">AI思考中...</span>
              )}
              {gameState.selectedPiece && (
                <span className="text-xs text-green-600 ml-2">
                  {(() => {
                    const selectedAnimal = gameState.board[gameState.selectedPiece.row][gameState.selectedPiece.col];
                    if (selectedAnimal) {
                      const moveCount = gameState.possibleMoves?.length || 0;
                      const captureMoves = gameState.possibleMoves?.filter(m => m.captures && m.captures.length > 0).length || 0;
                      return captureMoves > 0 ? `${ANIMAL_NAMES[selectedAnimal.type]}可吃子${captureMoves}次` : `${ANIMAL_NAMES[selectedAnimal.type]}可移动${moveCount}格`;
                    }
                    return '';
                  })()}
                </span>
              )}
            </div>

            {/* 步数和动物信息 */}
            <div className="flex items-center space-x-3 text-sm sm:text-base text-gray-600">
              <span>步数: {gameState.totalMoves}</span>
              <span>黑方: {gameState.animals.black.length}只</span>
              <span>白方: {gameState.animals.white.length}只</span>
            </div>
          </div>
        </div>
      )}

      {/* 游戏结束状态 */}
      {gameState.status === 'finished' && (
        <div className="bg-white shadow p-2">
          <div className="text-center">
            <div className="text-lg sm:text-xl font-bold text-green-600 mb-1">
              🏆 {gameState.winner === 'black' ? '黑方' : '白方'}获胜！
            </div>
            <div className="text-sm text-gray-600">
              总步数: {gameState.totalMoves} • 吃子: {gameState.captureMoves} • 跳河: {gameState.riverJumps}
            </div>
          </div>
        </div>
      )}

      {/* 棋盘 - 移动端满屏布局 */}
      <div className="flex-1 flex items-center justify-center px-1 py-1 xs:px-1 xs:py-2">
        <div className="w-full h-full max-w-none">
          <JungleBoard
            gameState={gameState}
            onCellClick={handleCellClick}
            disabled={gameState.status !== 'playing' || gameState.isAIThinking}
            enlarged={isBoardEnlarged}
            gameMode={gameMode}
          />
        </div>
      </div>

      {/* 游戏规则提示 - 统一布局 */}
      <div className="bg-green-50 border-t border-green-200 p-2 sm:p-3">
        <p className="text-sm sm:text-base text-green-800 text-center leading-relaxed">
          <strong>斗兽棋规则：</strong>
          动物等级 象(8)＞狮(7)＞虎(6)＞豹(5)＞狼(4)＞狗(3)＞猫(2)＞鼠(1) |
          高等级吃低等级 | 鼠可吃象 | 只有鼠可入河 | 狮虎可跳河 |
          占领对方巢穴或吃光对方动物获胜 | {gameMode === 'pvc' ? '人机对战' : '双人对战'}
        </p>
      </div>

      {/* 胜利特效 */}
      {showVictoryEffect && lastWinner && (
        <VictoryEffect
          winner={lastWinner}
          isVisible={showVictoryEffect}
          onHide={() => {
            setShowVictoryEffect(false);
            handleRestart();
          }}
        />
      )}

      {/* 游戏消息 */}
      {gameMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg font-medium text-lg">
            {gameMessage}
          </div>
        </div>
      )}

      {/* 游戏规则对话框 */}
      {showRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            {/* 关闭按钮 */}
            <button
              onClick={() => setShowRules(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold text-center mb-4 text-gray-800 pr-8">
              🐯 斗兽棋游戏规则
            </h2>

            <div className="text-gray-700 space-y-4 mb-6">
              <div>
                <h3 className="font-bold text-lg mb-2">游戏目标</h3>
                <p>占领对方巢穴或吃光对方所有动物获胜！</p>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">动物等级</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>象(8) ＞ 狮(7) ＞ 虎(6) ＞ 豹(5) ＞ 狼(4) ＞ 狗(3) ＞ 猫(2) ＞ 鼠(1)</li>
                  <li>高等级动物可以吃低等级动物</li>
                  <li>特殊规则：老鼠可以吃大象</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">特殊地形规则</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li><strong>河流：</strong>只有老鼠可以进入河流</li>
                  <li><strong>跳河：</strong>狮子和老虎可以跳过河流</li>
                  <li><strong>巢穴：</strong>己方动物不能进入己方巢穴</li>
                  <li><strong>陷阱：</strong>进入对方陷阱的动物战斗力变为0</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">移动规则</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>动物每次只能移动一格</li>
                  <li>只能上下左右移动，不能斜向移动</li>
                  <li>不能移动到已有己方动物的位置</li>
                  <li>可以移动到对方动物的位置进行吃子</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">获胜条件</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>任意己方动物占领对方巢穴</li>
                  <li>吃光对方所有动物</li>
                  <li>对方无法移动（被困死）</li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold text-lg mb-2">策略提示</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>合理利用老鼠的特殊能力：可以吃象，可以入河</li>
                  <li>狮子和老虎的跳河能力很重要，可以快速进攻</li>
                  <li>保护己方巢穴，防止被偷袭</li>
                  <li>合理设置陷阱，削弱对方强力的动物</li>
                  <li>大象虽然最强，但要小心对方的老鼠</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setShowRules(false)}
              className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              我知道了
            </button>
          </div>
        </div>
      )}

      {/* 游戏结束对话框 */}
      {showEndGameDialog && lastWinner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl relative">
            {/* 关闭按钮 */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowEndGameDialog(false);
                setDialogManuallyClosed(true);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>
            <h2 className="text-2xl font-bold text-center mb-4">
              <span className={lastWinner === 'black' ? 'text-gray-800' : 'text-gray-600'}>
                {lastWinner === 'black' ? '黑方' : '白方'}获胜！
              </span>
            </h2>

            <div className="text-center text-gray-600 mb-6">
              <p>游戏统计：</p>
              <p>总步数: {gameState.totalMoves}</p>
              <p>吃子数: {gameState.captureMoves}</p>
              <p>跳河数: {gameState.riverJumps}</p>
              {gameMode === 'pvc' && (
                <p>AI难度: {difficulty === 'easy' ? '简单' : difficulty === 'medium' ? '中等' : '困难'}</p>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={handleRestart}
                className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                再来一局
              </button>
              {onBackToMenu && (
                <button
                  onClick={onBackToMenu}
                  className="w-full bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  返回主页
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JungleGame;